<template>
  <div class="animated fadeIn">
    <b-form @submit.prevent="submitForm">
      <b-row class="mt-4">
        <b-col>
          <b-form-group label="Start Date" :label-cols="2" :horizontal="true">
            <b-form-input
              type="date"
              v-model="form_data.starts_at"
            />
          </b-form-group>

          <b-form-group label="End Date" :label-cols="2" :horizontal="true">
            <b-form-input
              type="date"
              v-model="form_data.ends_at"
            />
          </b-form-group>

          <b-form-group label="Interval" :label-cols="2" :horizontal="true">
            <b-form-select
              v-model="form_data.interval"
              :options="[
                { text: 'Week', value: 'week' },
                { text: 'Month', value: 'month' },
                { text: 'Quarter', value: 'quarter' },
                { text: 'Year', value: 'year' }
              ]"
            />
          </b-form-group>

          <b-form-group
            label="Chart Type:"
            label-cols="2">
            <b-form-radio-group
              v-model="chart_type"
              :options="[
              { text: 'Line', value: 'line' },
              { text: 'Bar', value: 'bar' },
            ]"
            />
          </b-form-group>

          <b-form-group label="Sample Type" label-cols="2">
            <i v-if="sample_type_loading" class="fal fa-spinner fa-spin fa-lg mt-2" />
            <CustomFormRadioGroup
              v-else
              v-model="form_data.sample_type_id"
              :options="sample_type_options"
            />
          </b-form-group>

          <div v-if="selectedSampleTypeModal && ['Soil', 'Tissue'].includes(selectedSampleTypeModal.name)">
            <h3>{{ selectedSampleTypeModal.name }} Data</h3>

            <template v-if="['Soil', 'Tissue'].includes(selectedSampleTypeModal.name)">
              <b-form-group
                label="Grower"
                :label-cols="2"
                :horizontal="true"
              >
                <b-form-input
                  type="text"
                  v-model="form_data.grower"
                  placeholder="Grower"
                />
              </b-form-group>

              <b-form-group
                label="Field"
                :label-cols="2"
                :horizontal="true"
              >
                <b-form-input
                  type="text"
                  v-model="form_data.field"
                  placeholder="Field"
                />
              </b-form-group>
            </template>

            <template v-if="selectedSampleTypeModal.name === 'Soil'">
              <b-form-group
                label="Zone"
                :label-cols="2"
                :horizontal="true"
              >
                <b-form-input
                  type="text"
                  v-model="form_data.zone"
                  placeholder="Zone"
                />
              </b-form-group>
            </template>

            <template v-if="selectedSampleTypeModal.name === 'Tissue'">
              <b-form-group
                label="Crop"
                :label-cols="2"
                :horizontal="true"
              >
                <add-on-select
                  :selectedItem="form_data.crop"
                  :options="cropOptions"
                  :customPlaceholder="'Select Crop Type'"
                  @change="({ selected, field }) => { form_data.crop = selected }"
                />
              </b-form-group>
            </template>
          </div>
        </b-col>
      </b-row>

      <!-- validation error -->
      <b-row v-if="invalid" class="mt-4">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-button
            type="submit"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            Submit
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import CustomFormRadioGroup from '@/components/shared/CustomFormRadioGroup';
import AddOnSelect from '@/components/submissions/AddOnSelect.vue';
import SampleTypeCollection from '../../../collections/lab/SampleTypeCollection';
import ManagedListCollection from '../../../collections/lab/ManagedListCollection';
import moment from "moment";

export default {
  name: 'TimeSeriesReportForm',
  components: {
    CustomFormRadioGroup,
    AddOnSelect
  },
  mixins: [validationMixin],
  props: {
    result: {
      type: Object,
      default: () => null
    },
    set_customer_grower: {
      type: Object,
      default: () => null
    },
    submission: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      form_data: {
        sample_type_id: null,
        starts_at: null,
        ends_at: null,
        interval: 'week', // ENUM: week, month, quarter, year

        // soil and tissue specific fields
        field: null,
        grower: null,

        // soil specific fields
        zone: null,

        // tissue specific fields
        crop: null,
      },

      chart_type: 'line',

      sample_type_loading: false,
      sample_type_collection: null,
      sample_type_options: [],
      loading_test_package: false,

      managed_list_collection: null,

      crop_options: null
    }
  },
  validations: {
    form_data: {
    }
  },
  computed: {
    invalid () {
      return this.$v && this.$v.$dirty && this.$v.$invalid
    },

    selectedSampleTypeModal () {
      return this.sample_type_collection ? this.sample_type_collection.models.find((sample_type) => {
        return sample_type.id === this.form_data.sample_type_id
      }) : null
    },

    managedLists() {
      return this.managed_list_collection &&
        this.managed_list_collection.models &&
        this.managed_list_collection.models.length
        ? this.managed_list_collection.models
        : [];
    },

    cropOptions () {
      return this.createOptionList(
        this.managedLists.find(
          item => item.sub_type === "tissue" && item.list === "type"
        ).attribute_lists,
        "crop"
      )
    }
  },
  async created () {
    this.fetchSampleTypes()

    if (this.result) {
      this.form_data.sample_type_id = this.result.sample_type_id
      this.form_data.grower = this.result.grower
      this.form_data.field = this.result.field
      this.form_data.zone = this.result.zone

      this.form_data.starts_at = moment(this.result.collected_at).format('YYYY-01-01')
      this.form_data.ends_at = moment(this.result.collected_at).format('YYYY-MM-DD')
    }

    if (this.set_customer_grower) {
      this.form_data.grower = this.set_customer_grower.name
    }

    if (this.submission) {
      this.form_data.sample_type_id = this.submission.sample_type_id
      this.form_data.grower = this.submission.submission_soil ? this.submission.submission_soil.grower : null
      this.form_data.field = this.submission.submission_soil ? this.submission.identifier : null
      this.form_data.zone = this.submission.submission_soil ? this.submission.submission_soil.zone : null
    }

    this.managed_list_collection = new ManagedListCollection()
    await this.managed_list_collection.fetch()
  },
  methods: {
    async fetchSampleTypes () {
      this.sample_type_loading = true
      this.sample_type_collection = new SampleTypeCollection()
      await this.sample_type_collection.fetch()
      this.sample_type_loading = false

      this.sample_type_options = this.sample_type_collection.models.map((sample_type) => {
        return {
          text: sample_type.name,
          value: sample_type.id,
          icon: 'cube'
        }
      })
    },

    createOptionList(list, field) {
      return {
        options: list.map(item => {
          return { id: item.id, name: item.value, parent_id: item.parent_id };
        }),
        field
      }
    },

    submitForm () {
      this.$v.$touch()
      if (this.invalid) return

      this.$emit('submit', {
        sample_type_id: this.form_data.sample_type_id,
        interval: this.form_data.interval,
        starts_at: this.form_data.starts_at,
        ends_at: this.form_data.ends_at,
        filters: [
          ... this.form_data.zone ? [{ field: 'zone', value: this.form_data.zone }] : [],
          ... this.form_data.field ? [{ field: 'field', value: this.form_data.field }] : [],
          ... this.form_data.grower ? [{ field: 'grower', value: this.form_data.grower }] : [],
          ... this.form_data.crop ? [{ field: 'crop', value: this.form_data.crop }] : []
        ]
      })

      this.$emit('setChartType', this.chart_type)
    }
  }
}
</script>
